import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MessageSquare, Play, Award, CheckCircle, Loader2 } from 'lucide-react';
import { firmInfo } from '../../data/mockData';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HeroSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
    contactMethod: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      await axios.post(`${BACKEND_URL}/api/contact`, formData);
      setSubmitStatus({ 
        type: 'success', 
        message: 'Thank you for your inquiry. We will contact you shortly!' 
      });
      setFormData({ name: '', email: '', phone: '', message: '', contactMethod: '' });
    } catch (error) {
      setSubmitStatus({ 
        type: 'error', 
        message: 'Failed to send message. Please try again or call us directly.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="relative min-h-screen flex items-center bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Background Pattern */}
      <div className="absolute inset-0 z-0 opacity-30">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-blue-100/50 to-transparent"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 w-full">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            {/* Settlement Badge */}
            <div className="inline-flex items-center gap-3 bg-blue-900/10 border border-blue-900/20 rounded-full px-4 py-2">
              <Award className="w-5 h-5 text-amber-600" />
              <span className="text-blue-900 font-semibold text-sm">
                {firmInfo.largestSettlement} - {firmInfo.settlementTitle}
              </span>
            </div>

            {/* Main Headline */}
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 leading-tight">
              {firmInfo.tagline.split(' ').slice(0, 2).join(' ')}{' '}
              <span className="text-blue-900">
                {firmInfo.tagline.split(' ').slice(2).join(' ')}
              </span>
            </h1>

            <p className="text-xl text-gray-600 max-w-xl">
              Over {firmInfo.yearsExperience} years of trial-tested experience fighting for injury victims.
              No fee unless we win your case.
            </p>

            {/* Trust Indicators */}
            <div className="flex flex-wrap gap-4">
              {['Free Consultation', 'No Win, No Fee', '24/7 Available'].map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 text-gray-700">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-sm font-medium">{item}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4">
              <Button
                asChild
                size="lg"
                className="bg-blue-900 hover:bg-blue-800 text-white font-bold px-8 py-6 text-lg rounded-md transition-all duration-300 hover:shadow-lg hover:shadow-blue-900/30"
              >
                <a href={`tel:${firmInfo.phoneRaw}`}>
                  <Phone className="w-5 h-5 mr-2" />
                  Call Now
                </a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-2 border-blue-900 text-blue-900 hover:bg-blue-900 hover:text-white font-bold px-8 py-6 text-lg rounded-md transition-all duration-300"
              >
                <Link to="/about">
                  <Play className="w-5 h-5 mr-2" />
                  Learn More
                </Link>
              </Button>
            </div>

            {/* Quick Contact */}
            <div className="flex flex-wrap gap-6 pt-4">
              <a
                href={`tel:${firmInfo.phoneRaw}`}
                className="flex items-center gap-2 text-gray-700 hover:text-blue-900 transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Phone className="w-5 h-5 text-blue-900" />
                </div>
                <span className="font-medium">Call</span>
              </a>
              <a
                href={`mailto:${firmInfo.email}`}
                className="flex items-center gap-2 text-gray-700 hover:text-blue-900 transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <Mail className="w-5 h-5 text-blue-900" />
                </div>
                <span className="font-medium">Email</span>
              </a>
              <a
                href={`sms:${firmInfo.phoneRaw}`}
                className="flex items-center gap-2 text-gray-700 hover:text-blue-900 transition-colors"
              >
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-blue-900" />
                </div>
                <span className="font-medium">Text</span>
              </a>
            </div>
          </div>

          {/* Right Content - Contact Form */}
          <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-xl shadow-gray-200/50">
            <div className="text-center mb-6">
              <p className="text-blue-900 text-sm font-semibold uppercase tracking-wider mb-2">
                Click Here For
              </p>
              <h2 className="text-2xl font-bold text-gray-900">
                <span className="text-amber-600">FREE</span> INJURY CASE REVIEW
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {submitStatus.message && (
                <div className={`p-4 rounded-lg text-sm ${
                  submitStatus.type === 'success' 
                    ? 'bg-green-50 text-green-700 border border-green-200' 
                    : 'bg-red-50 text-red-700 border border-red-200'
                }`}>
                  {submitStatus.message}
                </div>
              )}
              <Input
                type="text"
                placeholder="Your Name *"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-500 focus:border-blue-500 focus:ring-blue-500"
              />
              <Input
                type="email"
                placeholder="Your Email *"
                required
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-500 focus:border-blue-500 focus:ring-blue-500"
              />
              <Input
                type="tel"
                placeholder="Your Phone *"
                required
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-500 focus:border-blue-500 focus:ring-blue-500"
              />
              <Select
                value={formData.contactMethod}
                onValueChange={(value) => setFormData({ ...formData, contactMethod: value })}
              >
                <SelectTrigger className="bg-gray-50 border-gray-200 text-gray-900 focus:border-blue-500 focus:ring-blue-500">
                  <SelectValue placeholder="Preferred method of contact" />
                </SelectTrigger>
                <SelectContent className="bg-white border-gray-200">
                  <SelectItem value="email" className="text-gray-900 hover:bg-gray-100">Email</SelectItem>
                  <SelectItem value="phone" className="text-gray-900 hover:bg-gray-100">Phone</SelectItem>
                </SelectContent>
              </Select>
              <Textarea
                placeholder="Tell us about your case..."
                rows={4}
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                className="bg-gray-50 border-gray-200 text-gray-900 placeholder:text-gray-500 focus:border-blue-500 focus:ring-blue-500 resize-none"
              />
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-blue-900 hover:bg-blue-800 text-white font-bold py-6 text-lg rounded-md transition-all duration-300 hover:shadow-lg disabled:opacity-50"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    SENDING...
                  </>
                ) : (
                  'SEND MESSAGE'
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
